import { useCallback, useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { HStack, PrimaryButton } from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { DeleteConfirmDialog } from '~/components/common/DeleteConfirmDialog'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import type {
  DeleteErrorInfo,
  ModalState,
} from '~/pages/AdminOcctoMaster/shell/types'
import { useMasterInlineDelete } from '~/pages/AdminOcctoMaster/shell/useMasterInlineDelete'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { CreateGenerationSiteModal } from '~/pages/AdminPlantUnitMaster/components/CreateGenerationSiteModal'
import { EditGenerationSiteModal } from '~/pages/AdminPlantUnitMaster/components/EditGenerationSiteModal'
import { GenerationSiteTable } from '~/pages/AdminPlantUnitMaster/components/GenerationSiteTable'
import { useDeleteGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useDeleteGenerationSite'
import { useListGenerationSites } from '~/pages/AdminPlantUnitMaster/hooks/useListGenerationSites'
import { GENERATION_SITE_ERROR_KEY_PREFIX } from '~/pages/AdminPlantUnitMaster/models/serverError'
import {
  type GenerationSiteRow,
  type SiteSort,
  convertListGenerationSitesResponse,
  filterAndSortGenerationSites,
} from '~/pages/AdminPlantUnitMaster/models/siteRows'

/**
 * 電源ユニット画面の「発電所」タブ = 自社の発電所 (サイト) の一覧・登録・編集・削除。
 * エリア (業務エリア設定で絞った選択肢) とキーワード (名称・略称) で絞り込む。
 * 配下の電源ユニット数 (backend が数えた値) を列に出し、ユニットのある発電所の削除は backend が止める。
 */
export const GenerationSiteTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListGenerationSites()
  const [areaCode, setAreaCode] = useState<AreaCode>(AreaCode.UNSPECIFIED)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)
  const [modalState, setModalState] = useState<ModalState<GenerationSiteRow>>({
    kind: 'none',
  })
  const [deleteError, setDeleteError] = useState<DeleteErrorInfo | undefined>(
    undefined,
  )

  const rows = useMemo(
    () => (data !== undefined ? convertListGenerationSitesResponse(data) : []),
    [data],
  )
  const [sort, setSort] = useState<SiteSort | undefined>(undefined)
  const visibleRows = useMemo(
    () =>
      filterAndSortGenerationSites(rows, areaCode, deferredSearchText, sort),
    [rows, areaCode, deferredSearchText, sort],
  )
  // エリア不一致 (発電所と、配下ユニットの系統コードのエリアが違う): 一覧上部の注意の件数 (判定は backend の応答値)
  const mismatchedSiteCount = useMemo(
    () => rows.filter((row) => row.areaMismatchedUnits.length > 0).length,
    [rows],
  )

  const closeModal = useCallback(() => {
    setModalState({ kind: 'none' })
    setDeleteError(undefined)
  }, [])

  const { handleDeleteConfirm, isDeleting } =
    useMasterInlineDelete<GenerationSiteRow>({
      modalState,
      closeModal,
      setDeleteError,
      errorKeyPrefix: GENERATION_SITE_ERROR_KEY_PREFIX,
      useDelete: useDeleteGenerationSite,
    })

  const errorLabel =
    isError && data === undefined
      ? mapMasterGetError(t, error, GENERATION_SITE_ERROR_KEY_PREFIX)
      : undefined
  const showInitialLoading = isLoading && data === undefined
  const deleteRow = modalState.kind === 'delete' ? modalState.row : null

  return (
    <>
      {mismatchedSiteCount > 0 && (
        <CompactAlert
          description={t(
            'page:adminPlantUnitMaster.site.areaMismatchDescription',
          )}
          status="warning"
          title={t('page:adminPlantUnitMaster.site.areaMismatchTitle', {
            count: mismatchedSiteCount,
          })}
        />
      )}
      <MasterFilterToolbar
        onSearchTextChange={setSearchText}
        onSelectArea={setAreaCode}
        searchPlaceholder={t(
          'page:adminPlantUnitMaster.site.searchPlaceholder',
        )}
        searchText={searchText}
        selectedAreaCode={areaCode}
      />
      <HStack
        justify="flex-end"
        flexShrink={0}
        gap={2}
        marginTop={4}
        paddingTop={2}
        paddingBottom={2}
      >
        <PrimaryButton
          leftIcon={<FaPlusRegular />}
          onClick={() => setModalState({ kind: 'create' })}
        >
          {t('page:adminPlantUnitMaster.add')}
        </PrimaryButton>
      </HStack>
      <RegionLoading
        flexDirection="column"
        flex="1"
        display="flex"
        minHeight={showInitialLoading ? '200px' : 0}
        isLoading={showInitialLoading}
      >
        {showInitialLoading ? null : (
          <PageScrollArea>
            <GenerationSiteTable
              errorLabel={errorLabel}
              isLoading={isLoading}
              onDelete={(row) => {
                setDeleteError(undefined)
                setModalState({ kind: 'delete', row })
              }}
              onEdit={(row) => setModalState({ kind: 'edit', row })}
              onSortChange={setSort}
              rows={visibleRows}
              sort={sort}
            />
          </PageScrollArea>
        )}
      </RegionLoading>
      {/* 条件 mount (閉じたら form ごと破棄): 次に開いた時に前回の入力が残らない。 */}
      {modalState.kind === 'create' && (
        <CreateGenerationSiteModal
          defaultAreaCode={areaCode}
          isOpen
          onClose={closeModal}
        />
      )}
      {modalState.kind === 'edit' && (
        <EditGenerationSiteModal
          row={modalState.row}
          isOpen
          onClose={closeModal}
        />
      )}
      {deleteRow !== null && (
        <DeleteConfirmDialog
          entityName={t('page:adminPlantUnitMaster.site.deleteEntityName', {
            name: deleteRow.siteName,
          })}
          errorIsRetriable={deleteError?.retriable}
          errorMessage={deleteError?.message}
          isLoading={isDeleting}
          isOpen
          onCancel={closeModal}
          onConfirm={() => {
            void handleDeleteConfirm()
          }}
          title={t('page:adminPlantUnitMaster.site.delete.confirmTitle')}
        />
      )}
    </>
  )
}
